import React, { forwardRef } from 'react';
import ReactDatePicker, {
    DatePickerProps as ReactDatePickerProps,
    registerLocale,
    setDefaultLocale,
} from 'react-datepicker';
import { IMaskInput } from 'react-imask';
import classnames from 'classnames';
import isNull from 'lodash/isNull';
import { ru } from 'date-fns/locale/ru';

import './Datepicker_.scss';
import 'react-datepicker/dist/react-datepicker.css';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { Icon, IconType } from '@components/Icon';
import { FieldTitle, FieldTitleTheme } from '@components/FieldTitle';
import { DateHelper } from '@utils/DateHelper';
import { ClearIcon } from '@components/Select/Select';

import { useDatePicker } from './hooks/useDatePicker';

export type DatePickerProps = Omit<ReactDatePickerProps, 'onChange' | 'selectsMultiple' | 'minDate' | 'maxDate'> & {
    wrapperClassName?: string;
    title?: string;
    dataTest?: string;
    maxDate?: Nullable<Date>;
    minDate?: Nullable<Date>;
    isValid?: Nullable<boolean>;
    showValidStatus?: boolean;
    customDateFormat?: string;
    customMask?: string;
    placeholder?: string;
    boldTitle?: boolean;
    withoutCalendarIcon?: boolean;
    withoutMobileCentering?: boolean;
    alwaysShowMask?: boolean;
    modifiers?: string[];
    onChange(date: Date, event?: any, ...args: any[]): void;
};

export const DatePickerModifiers = {
    RedesignedLabel: 'date_picker__redesignedLabel',
};

interface Props extends Omit<DatePickerProps, 'onChange'> {
    name?: string;
    onChange(
        date: Date,
        name?: string,
        event?: React.SyntheticEvent<any> | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        ...args: any[]
    ): void;
}

type ReactDatePickerOnChange = (
    date: Date | [Date | null, Date | null] | null,
    event?: React.SyntheticEvent<any> | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | undefined,
) => void;

registerLocale('ru', ru);
setDefaultLocale('ru');

const DATE_FORMAT_MASK_MAP: Record<string, string> = {
    'dd.MM.yyyy': '00.00.0000',
    'MM.yyyy': '00.0000',
    'HH:mm': '00:00',
    'dd.MM.yyyy HH:mm': '00.00.0000 00:00',
};

interface MaskedDateInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    mask: string;
    lazy?: boolean;
}

const MaskedDateInput = forwardRef<HTMLInputElement, MaskedDateInputProps>(({ mask, lazy = true, ...props }, ref) => (
    // @ts-expect-error
    <IMaskInput
        {...props}
        mask={mask}
        lazy={lazy}
        // @ts-ignore
        inputRef={ref}
    />
));
MaskedDateInput.displayName = 'MaskedDateInput';

export function DatePicker({
    dataTest,
    wrapperClassName,
    title,
    customDateFormat,
    customMask,
    children,
    isValid = null,
    showValidStatus = false,
    placeholder,
    boldTitle,
    disabled,
    selected,
    withoutCalendarIcon = false,
    withoutMobileCentering = false,
    alwaysShowMask,
    modifiers = [],
    name,
    onChange,
    ...props
}: Props) {
    const { isOnFocus, handleChange, handleClear, handleInputBlur, captureTypedValue, handleOpen } = useDatePicker({
        selected,
        minDate: props.minDate,
        maxDate: props.maxDate,
        isClearable: props.isClearable,
        customDateFormat,
        portalId: props.portalId,
        withoutMobileCentering,
        name,
        onChange,
    });

    const withValidStatus = showValidStatus && !isNull(isValid);
    const showClearIcon = props.isClearable && !!selected && !disabled;

    return (
        <div
            className={classnames([
                'date_picker__root',
                isOnFocus && 'date_picker__root_open',
                withValidStatus && (isValid ? 'date_picker__valid' : 'date_picker__not_valid'),
                withoutCalendarIcon && 'date_picker__withoutCalendarIcon',
                !withoutMobileCentering && 'date_picker__withMobileCentering',
                disabled && 'date_picker__root_disabled',
                props.inline && 'date_picker__root_inline',
                ...modifiers,
                wrapperClassName,
            ])}
        >
            {!withoutCalendarIcon && (
                <div className={'calendarIcon'}>
                    <Icon type={props.showTimeSelectOnly ? IconType.CLOCK : IconType.CALENDAR_REDESIGN} />
                </div>
            )}
            <div className={classnames([title && 'title'])}>
                {title && (
                    <FieldTitle
                        theme={FieldTitleTheme.WithFlex}
                        title={title}
                    />
                )}
            </div>
            {showClearIcon && (
                <div
                    className={classnames(['date_picker__clear_icon', withoutCalendarIcon && 'singleIcon'])}
                    onClick={handleClear}
                >
                    <ClearIcon />
                </div>
            )}
            <ReactDatePicker
                {...(props as any)}
                onChange={
                    ((date, event) => {
                        handleChange(date as Nullable<Date>, event);
                    }) as ReactDatePickerOnChange
                }
                selected={!!selected ? (DateHelper.isValid(selected) ? selected : null) : selected}
                disabled={disabled}
                customInput={
                    <MaskedDateInput
                        lazy
                        data-test={dataTest}
                        data-testid={dataTest}
                        mask={customMask || DATE_FORMAT_MASK_MAP[customDateFormat || 'dd.MM.yyyy'] || '00.00.0000'}
                        onInput={captureTypedValue}
                    />
                }
                placeholderText={title ? '' : placeholder}
                className={'date_picker__input'}
                dateFormat={customDateFormat || 'dd.MM.yyyy'}
                onFocus={handleOpen}
                onBlur={handleInputBlur}
                onChangeRaw={captureTypedValue}
                minDate={props.minDate || undefined}
                maxDate={props.maxDate || undefined}
                popperModifiers={[
                    {
                        name: 'preventOverflow',
                        options: {
                            enabled: true,
                        },
                        fn: state => state,
                    },
                ]}
            >
                {children}
            </ReactDatePicker>
        </div>
    );
}
